import axios, { AxiosInstance, AxiosRequestConfig, CreateAxiosDefaults } from 'axios';
import * as Sentry from '@sentry/react';
import { reportRejectedSession } from '@/services/authSessionRecovery';
import {
  buildNetworkRetryBreadcrumb,
  NetworkRetryConfig,
  NETWORK_RETRY_DELAY_MS,
  shouldRetryNetworkError,
} from './networkRetry';

export const isAxiosError = axios.isAxiosError;
export const isCancel = axios.isCancel;

// Backward compatibility for a rolling deploy: older backend instances used
// 401 for these business-validation failures. Treating either as an expired
// session would log out a valid user (and an auto-join invite could loop).
const LEGACY_NON_SESSION_401_DETAILS = new Set([
  'Incorrect league password.',
  'Invalid invite link.',
]);

const bearerTokenFromHeaders = (headers: unknown): string | null => {
  if (!headers || typeof headers !== 'object') return null;

  const headerBag = headers as {
    get?: (name: string) => unknown;
    Authorization?: unknown;
    authorization?: unknown;
  };
  const authorization = typeof headerBag.get === 'function'
    ? headerBag.get('Authorization')
    : headerBag.Authorization ?? headerBag.authorization;

  if (typeof authorization !== 'string') return null;
  const match = authorization.match(/^Bearer\s+(.+)$/i);
  return match?.[1] || null;
};

const waitForNetworkRetry = (config: NetworkRetryConfig): Promise<void> => (
  new Promise((resolve, reject) => {
    const signal = config.signal;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (timeoutId !== null) clearTimeout(timeoutId);
      signal?.removeEventListener?.('abort', handleAbort);
    };
    const handleAbort = () => {
      cleanup();
      reject(new axios.CanceledError('canceled'));
    };

    if (signal?.aborted) {
      handleAbort();
      return;
    }
    signal?.addEventListener?.('abort', handleAbort, { once: true });
    timeoutId = setTimeout(() => {
      cleanup();
      resolve();
    }, NETWORK_RETRY_DELAY_MS);
  })
);

/**
 * Create an API client that reports rejected authenticated sessions once.
 *
 * The request still rejects so callers cannot continue as if their operation
 * succeeded. AuthProvider consumes the report before page-level error UI can
 * leave the user stranded on stale authenticated content.
 *
 * Idempotent reads that die in transit are replayed once (see networkRetry).
 */
export const createApiClient = (
  config: CreateAxiosDefaults = {},
): AxiosInstance => {
  const client = axios.create(config);
  client.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      if (shouldRetryNetworkError(error)) {
        // The replay's own failure comes back through this handler with
        // networkRetried set and is rejected like any other error.
        const retryConfig = error.config as NetworkRetryConfig & AxiosRequestConfig;
        retryConfig.networkRetried = true;
        return waitForNetworkRetry(retryConfig).then(() => {
          try {
            Sentry.addBreadcrumb(buildNetworkRetryBreadcrumb(error));
          } catch { /* telemetry must never break the request */ }
          return client.request(retryConfig);
        });
      }
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        const responseDetail = error.response.data?.detail;
        if (
          typeof responseDetail === 'string'
          && LEGACY_NON_SESSION_401_DETAILS.has(responseDetail)
        ) return Promise.reject(error);

        const token = bearerTokenFromHeaders(error.config?.headers);
        if (token) {
          reportRejectedSession({
            token,
            // Query strings can contain user-entered filters or opaque
            // credentials. Recovery telemetry needs only the endpoint path.
            requestPath: error.config?.url?.split('?')[0],
          });
        }
      }
      return Promise.reject(error);
    },
  );
  return client;
};
